class GltheaaProductTabs extends HTMLElement {
  connectedCallback() {
    this.tablist = this.querySelector('[role="tablist"]');
    this.indicator = this.querySelector('.gltheaa-product-tabs__indicator');
    this.stage = this.querySelector('.gltheaa-product-tabs__stage');
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (!this.tablist || this.tabs.length === 0 || !this.stage) return;

    this.onTabClick = this.onTabClick.bind(this);
    this.onTabKeydown = this.onTabKeydown.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onIndicatorEnd = this.onIndicatorEnd.bind(this);

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick);
      tab.addEventListener('keydown', this.onTabKeydown);
    });
    this.indicator?.addEventListener('transitionend', this.onIndicatorEnd);

    this.activeTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    this.activePanel = this.panels.find((panel) => panel.classList.contains('is-active')) || this.panels[0];

    this.classList.add('is-ready');
    this.panels.forEach((panel) => {
      panel.hidden = false;
    });
    this.moveIndicator(this.activeTab, false);
    this.setStageHeight(this.activePanel.scrollHeight, false);

    this.resizeObserver = new ResizeObserver(() => {
      if (this.animating || !this.activePanel) return;
      this.setStageHeight(this.activePanel.scrollHeight, false);
      this.moveIndicator(this.activeTab, false);
    });
    this.resizeObserver.observe(this.stage);
    this.panels.forEach((panel) => this.resizeObserver.observe(panel));

    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    this.tabs?.forEach((tab) => {
      tab.removeEventListener('click', this.onTabClick);
      tab.removeEventListener('keydown', this.onTabKeydown);
    });
    window.removeEventListener('resize', this.onResize);
    this.indicator?.removeEventListener('transitionend', this.onIndicatorEnd);
    this.resizeObserver?.disconnect();
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  onResize() {
    if (!this.activeTab || !this.activePanel) return;
    this.moveIndicator(this.activeTab, false);
    if (!this.animating) {
      this.setStageHeight(this.activePanel.scrollHeight, false);
    }
  }

  onTabClick(event) {
    this.select(event.currentTarget);
  }

  onTabKeydown(event) {
    const index = this.tabs.indexOf(event.currentTarget);
    if (index < 0) return;

    let nextIndex = index;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (index + 1) % this.tabs.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this.tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    const nextTab = this.tabs[nextIndex];
    this.select(nextTab);
    nextTab.focus();
  }

  onIndicatorEnd(event) {
    if (event.propertyName !== 'transform' && event.propertyName !== 'width') return;
    this.indicator.classList.remove('is-moving');
  }

  moveIndicator(tab, animate = true) {
    if (!this.indicator || !tab) return;

    const listRect = this.tablist.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const left = tabRect.left - listRect.left + this.tablist.scrollLeft;
    const width = tabRect.width;

    this.indicator.classList.toggle('is-moving', Boolean(animate) && !this.prefersReducedMotion());
    this.indicator.style.width = `${width}px`;
    this.indicator.style.transform = `translateX(${left}px)`;
  }

  setStageHeight(height, animate = true) {
    const reduced = this.prefersReducedMotion() || !animate;
    this.stage.style.transition = reduced ? 'none' : '';
    this.stage.style.height = `${height}px`;
  }

  select(tab) {
    if (!tab || tab === this.activeTab) return;

    const nextPanel = this.panels.find((panel) => panel.id === tab.getAttribute('aria-controls'));
    if (!nextPanel) return;

    this.tabs.forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.tabIndex = selected ? 0 : -1;
    });

    this.moveIndicator(tab, true);

    const previousPanel = this.activePanel;
    this.activeTab = tab;
    this.activePanel = nextPanel;

    if (this.prefersReducedMotion()) {
      previousPanel?.classList.remove('is-active');
      previousPanel?.setAttribute('aria-hidden', 'true');
      nextPanel.classList.add('is-active');
      nextPanel.setAttribute('aria-hidden', 'false');
      this.setStageHeight(nextPanel.scrollHeight, false);
      this.animating = false;
      return;
    }

    this.animating = true;
    this.setStageHeight(this.stage.offsetHeight, false);
    previousPanel?.classList.remove('is-active');
    previousPanel?.setAttribute('aria-hidden', 'true');
    nextPanel.classList.add('is-active');
    nextPanel.setAttribute('aria-hidden', 'false');

    const endHeight = nextPanel.scrollHeight;
    this.stage.offsetHeight;
    this.setStageHeight(endHeight, true);

    window.clearTimeout(this.heightTimer);
    this.heightTimer = window.setTimeout(() => {
      this.animating = false;
    }, 420);
  }
}

if (!customElements.get('gltheaa-product-tabs')) {
  customElements.define('gltheaa-product-tabs', GltheaaProductTabs);
}
